/* ══ ArcDroid global.js ═══════════════════════════════════════════════════════
   Owns: theme toggle, mobile menu, desktop nav dropdowns, announcement
   date-window, geo banner (Canada), footer accordion columns, generic
   accordion enhancement, qty steppers, drawer open/close wiring, region
   picker auto-submit. Loaded with defer — DOM is parsed when this runs.
   Cart mutations live in cart.js (events contract) — never here.
   ══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var AD = window.ArcDroid || {};
  window.ArcDroid = AD;

  /* Canada checkout-forward flag: swaps the checkout button for redirect-mode
     Canadian visitors (market-selected via Liquid, geo-detected via JS). */
  AD.checkoutForward = AD.canadaMode === 'redirect' && AD.region === 'ca';

  function applyCheckoutForward() {
    if (!AD.checkoutForward) return;
    var btns = document.querySelectorAll('button.cart-drawer__checkout, button.cart-page__checkout, button[name="checkout"]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].textContent = 'Checkout at arcdroid.ca ↗';
      btns[i].setAttribute('data-ca-forward', 'yes');
    }
  }
  document.addEventListener('cart:updated', applyCheckoutForward);
  document.addEventListener('DOMContentLoaded', applyCheckoutForward);
  document.addEventListener('click', function (e) {
    if (!AD.checkoutForward) return;
    var el = e.target instanceof Element ? e.target.closest('button.cart-drawer__checkout, button.cart-page__checkout, button[name="checkout"]') : null;
    if (!el) return;
    e.preventDefault();
    window.location.href = AD.canadaUrl || 'https://www.arcdroid.ca/';
  });

  function closestFrom(target, selector) {
    return target instanceof Element ? target.closest(selector) : null;
  }

  /* ── Theme toggle (localStorage 'ad-theme') ────────────────────────────── */

  function currentTheme() {
    return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
  }

  function syncThemeFollowers() {
    var t = currentTheme();
    document.querySelectorAll('[data-theme-follow]').forEach(function (el) {
      el.dataset.theme = t;
    });
  }

  function applyTheme(theme) {
    if (theme === 'light') {
      document.documentElement.dataset.theme = 'light';
    } else {
      delete document.documentElement.dataset.theme;
    }
    try {
      localStorage.setItem('ad-theme', theme);
    } catch (e) { /* private mode */ }
    syncThemeFollowers();
  }

  syncThemeFollowers();

  document.addEventListener('click', function (e) {
    var btn = closestFrom(e.target, '[data-theme-toggle]');
    if (!btn) return;
    applyTheme(currentTheme() === 'light' ? 'dark' : 'light');
  });

  /* ── Mobile menu ───────────────────────────────────────────────────────── */

  var burger = document.querySelector('[data-menu-toggle]');
  var mobilePanel = document.querySelector('[data-mobile-menu]');

  function sizeMobileMenu() {
    if (!mobilePanel || mobilePanel.hidden) return;
    /* cap to the real space below the panel's top edge — the CSS dvh cap can't
       account for the announcement/geo bars above the sticky header */
    var top = mobilePanel.getBoundingClientRect().top;
    mobilePanel.style.maxHeight = Math.max(200, window.innerHeight - top) + 'px';
  }

  function setMobileMenu(open) {
    if (!burger || !mobilePanel) return;
    burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    mobilePanel.hidden = !open;
    document.documentElement.classList.toggle('menu-open', open);
    if (open) {
      sizeMobileMenu();
      mobilePanel.focus({ preventScroll: true });
    } else {
      mobilePanel.style.maxHeight = '';
    }
  }

  window.addEventListener('resize', sizeMobileMenu);

  if (burger && mobilePanel) {
    burger.addEventListener('click', function () {
      setMobileMenu(mobilePanel.hidden);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !mobilePanel.hidden) {
        setMobileMenu(false);
        burger.focus();
      }
    });

    /* Close the panel if the viewport crosses into desktop layout. */
    var desktopMq = window.matchMedia('(min-width: 961px)');
    var onDesktopChange = function (mq) {
      if (mq.matches && !mobilePanel.hidden) setMobileMenu(false);
    };
    if (desktopMq.addEventListener) desktopMq.addEventListener('change', onDesktopChange);
    else desktopMq.addListener(onDesktopChange);
  }

  /* ── Desktop nav dropdowns ─────────────────────────────────────────────── */

  var HOVER_CLOSE_DELAY = 140;

  function setDropdown(item, open) {
    if (open) item.setAttribute('data-open', '');
    else item.removeAttribute('data-open');
    var btn = item.querySelector('[data-dropdown-toggle]');
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function closeAllDropdowns(except) {
    document.querySelectorAll('[data-nav-item][data-open]').forEach(function (item) {
      if (item !== except) setDropdown(item, false);
    });
  }

  document.querySelectorAll('[data-nav-item]').forEach(function (item) {
    var btn = item.querySelector('[data-dropdown-toggle]');
    if (!btn) return;
    var hoverTimer;

    btn.addEventListener('click', function () {
      var willOpen = !item.hasAttribute('data-open');
      closeAllDropdowns(item);
      setDropdown(item, willOpen);
    });

    item.addEventListener('mouseenter', function () {
      if (!window.matchMedia('(hover: hover)').matches) return;
      clearTimeout(hoverTimer);
      closeAllDropdowns(item);
      setDropdown(item, true);
    });

    item.addEventListener('mouseleave', function () {
      if (!window.matchMedia('(hover: hover)').matches) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function () {
        setDropdown(item, false);
      }, HOVER_CLOSE_DELAY);
    });

    item.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget)) setDropdown(item, false);
    });

    item.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && item.hasAttribute('data-open')) {
        e.stopPropagation();
        setDropdown(item, false);
        btn.focus();
      }
    });
  });

  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element) || !e.target.closest('[data-nav-item]')) {
      closeAllDropdowns(null);
    }
  });

  /* ── Announcement bar date window (client clock is the authority) ──────── */

  document.querySelectorAll('[data-announcement]').forEach(function (bar) {
    var starts = bar.getAttribute('data-starts');
    var ends = bar.getAttribute('data-ends');
    if (!starts && !ends) return;
    var now = Date.now();
    var show = true;
    if (starts) {
      var s = Date.parse(starts + 'T00:00:00');
      if (!isNaN(s) && now < s) show = false;
    }
    if (ends) {
      var en = Date.parse(ends + 'T23:59:59');
      if (!isNaN(en) && now > en) show = false;
    }
    bar.hidden = !show;
  });

  /* ── Geo banner (Canada) ───────────────────────────────────────────────── */

  (function geoBanner() {
    var banner = document.querySelector('[data-geo-banner]');
    if (!banner) return;

    var GEO_KEY = 'ad-geo-dismissed';
    var dismissed = false;
    try {
      dismissed = localStorage.getItem(GEO_KEY) === '1';
    } catch (e) { /* private mode */ }

    var dismissBtn = banner.querySelector('[data-geo-banner-dismiss]');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', function () {
        banner.hidden = true;
        try {
          localStorage.setItem(GEO_KEY, '1');
        } catch (e) { /* private mode */ }
      });
    }

    /* Dismissing hides the banner only. Checkout still has to be forwarded:
       this store doesn't ship to Canada, so a Canadian who clicked "Stay on
       the US store" must still be sent to arcdroid.ca at checkout. */
    if (dismissed) banner.hidden = true;

    if (AD.canadaMode !== 'redirect') return;

    /* Already on the Canadian market: banner is server-rendered visible and
       checkoutForward was set at the top of this file. */
    if (AD.region === 'ca') {
      if (!dismissed) banner.hidden = false;
      return;
    }

    /* Otherwise ask Shopify where the visitor is browsing from. */
    fetch('/browsing_context_suggestions.json')
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        if (!data) return;
        var cc = '';
        try {
          cc =
            (data.detected_values && data.detected_values.country && data.detected_values.country.handle) ||
            (data.suggestions &&
              data.suggestions[0] &&
              data.suggestions[0].parts &&
              data.suggestions[0].parts.country &&
              data.suggestions[0].parts.country.handle) ||
            '';
        } catch (e) { /* shape drift — stay hidden */ }
        if (String(cc).toUpperCase() === 'CA') {
          if (!dismissed) banner.hidden = false;
          if (AD.canadaMode === 'redirect') {
            AD.checkoutForward = true;
            applyCheckoutForward();
          }
        }
      })
      .catch(function () { /* offline / blocked — stay hidden */ });
  })();

  /* ── Footer columns: accordions on mobile, forced open on desktop ──────── */

  (function footerColumns() {
    var cols = document.querySelectorAll('details[data-footer-col]');
    if (!cols.length) return;
    var mq = window.matchMedia('(min-width: 768px)');

    function sync() {
      cols.forEach(function (d) {
        d.open = mq.matches;
      });
    }
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);

    /* Keyboard users can still toggle a summary desktop-side — snap it open. */
    cols.forEach(function (d) {
      d.addEventListener('toggle', function () {
        if (mq.matches && !d.open) d.open = true;
      });
    });
  })();

  /* ── Accordion enhancement: single-open groups ([data-accordion-single]) ─ */

  document.addEventListener(
    'toggle',
    function (e) {
      var d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.open) return;
      var group = d.closest('[data-accordion-single]');
      if (!group) return;
      group.querySelectorAll('details[open]').forEach(function (other) {
        if (other !== d && !other.contains(d) && !d.contains(other)) {
          other.open = false;
        }
      });
    },
    true
  );

  /* ── Quantity steppers (+/- update the input and emit 'change') ────────── */

  document.addEventListener('click', function (e) {
    var minus = closestFrom(e.target, '[data-qty-minus]');
    var plus = closestFrom(e.target, '[data-qty-plus]');
    if (!minus && !plus) return;
    var wrap = (minus || plus).closest('[data-qty]');
    var input = wrap && wrap.querySelector('input');
    if (!input) return;
    if (input.hasAttribute('data-line')) return; /* cart context — cart.js owns these steppers */

    var value = parseInt(input.value, 10);
    var min = input.min !== '' ? parseInt(input.min, 10) : 1;
    var max = input.max !== '' ? parseInt(input.max, 10) : Infinity;
    var step = parseInt(input.step, 10) || 1;
    if (isNaN(value)) value = min;

    value = plus ? value + step : value - step;
    value = Math.max(min, Math.min(max, value));

    if (String(value) !== input.value) {
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* ── Generic drawer wiring → drawer:open / drawer:close events ─────────── */

  document.addEventListener('click', function (e) {
    var opener = closestFrom(e.target, '[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      document.dispatchEvent(
        new CustomEvent('drawer:open', {
          detail: { id: opener.getAttribute('data-drawer-open') || 'CartDrawer', trigger: opener }
        })
      );
      return;
    }
    var closer = closestFrom(e.target, '[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      document.dispatchEvent(
        new CustomEvent('drawer:close', {
          detail: { id: closer.getAttribute('data-drawer-close') || '', trigger: closer }
        })
      );
    }
  });

  /* ── Region picker: auto-submit the localization form on change ────────── */

  document.addEventListener('change', function (e) {
    var select = closestFrom(e.target, '[data-region-select]');
    if (!select) return;
    var form = select.closest('form');
    if (form) form.submit();
  });
})();
